/**
 * Screenshot attachment metadata collection (plan §5.2 / D6).
 *
 * When a client attaches a screenshot we auto-capture the environment so a
 * support agent can reproduce the issue without a back-and-forth: browser and
 * version, OS, screen and viewport dimensions, device-pixel-ratio, timezone,
 * and the recent console-error ring buffer.
 *
 * The shape mirrors the `attachmentMetaSchema` contract in `@ventin/shared`
 * (WS1.2). It is intentionally duplicated locally so the pure-frontend capture
 * lane has zero backend dependency; the upload integration (WS2.5) validates
 * against the shared schema, which is the source of truth.
 */

import { getConsoleBuffer, type ConsoleEntry } from './consoleBuffer';

/** Auto-captured environment metadata for a screenshot attachment. */
export interface AttachmentMeta {
  browser?: string;
  browserVersion?: string;
  os?: string;
  screenW?: number;
  screenH?: number;
  viewportW?: number;
  viewportH?: number;
  dpr?: number;
  timezone?: string;
  /** ISO-8601 capture timestamp. */
  capturedAt?: string;
  consoleErrors?: ConsoleEntry[];
}

/** Non-standard `navigator.userAgentData` surface (Chromium only). */
interface UserAgentDataBrand {
  brand: string;
  version: string;
}
interface UserAgentData {
  brands?: UserAgentDataBrand[];
  platform?: string;
}

/**
 * Prefer the high-entropy `navigator.userAgentData` brand list (Chromium),
 * ignoring the "Not:A-Brand" GREASE entries, and fall back to a coarse
 * UA-string parse for Firefox/Safari.
 */
function detectBrowser(
  ua: string,
  uaData: UserAgentData | undefined,
): {
  browser?: string;
  browserVersion?: string;
} {
  const brands = uaData?.brands?.filter((b) => !/not.?a.?brand/i.test(b.brand));
  if (brands && brands.length > 0) {
    // Prefer a recognizable product brand over the generic "Chromium" entry.
    const preferred = brands.find((b) => !/chromium/i.test(b.brand)) ?? brands[0];
    return { browser: preferred!.brand, browserVersion: preferred!.version };
  }

  // UA-string fallback. Order matters: Edge/Opera masquerade as Chrome, and
  // Chrome's UA contains "Safari", so test the more specific tokens first.
  const patterns: Array<[string, RegExp]> = [
    ['Microsoft Edge', /Edg\/([\d.]+)/],
    ['Opera', /OPR\/([\d.]+)/],
    ['Firefox', /Firefox\/([\d.]+)/],
    ['Chrome', /Chrome\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+).*Safari/],
  ];
  for (const [name, re] of patterns) {
    const m = ua.match(re);
    if (m) return { browser: name, browserVersion: m[1] };
  }
  return {};
}

/** Coarse OS detection from `userAgentData.platform` or the UA string. */
function detectOs(ua: string, platform: string | undefined): string | undefined {
  if (platform) return platform;
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return undefined;
}

/**
 * Collect the current environment metadata. Every field is best-effort and
 * individually guarded — a missing/throwing browser API yields `undefined`
 * for that field rather than failing the whole capture.
 */
export function collectBrowserMeta(): AttachmentMeta {
  const nav: Navigator & { userAgentData?: UserAgentData } =
    typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const ua = nav.userAgent ?? '';
  const uaData = nav.userAgentData;

  const { browser, browserVersion } = detectBrowser(ua, uaData);

  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }

  return {
    browser,
    browserVersion,
    os: detectOs(ua, uaData?.platform),
    screenW: typeof screen !== 'undefined' ? screen.width : undefined,
    screenH: typeof screen !== 'undefined' ? screen.height : undefined,
    viewportW: typeof window !== 'undefined' ? window.innerWidth : undefined,
    viewportH: typeof window !== 'undefined' ? window.innerHeight : undefined,
    dpr: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
    timezone,
    capturedAt: new Date().toISOString(),
    consoleErrors: getConsoleBuffer(),
  };
}
